import { AbstractControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as queryString from 'query-string';

export class CustomValidators {
  static email(control: AbstractControl): ValidationErrors | null {
    const pattern = new RegExp(/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9.]+$/);
    const value = control.value;

    return pattern.test(value) ? null : { customValidate: 'Incorrect email' };
  }

  static userName(control: AbstractControl): ValidationErrors | null {
    const pattern = new RegExp(/^(?! )[a-zA-Z '-]+$/);
    const value = control.value;

    return pattern.test(value) ? null : { customValidate: 'Incorrect name' };
  }

  static offerUrl(control: AbstractControl): ValidationErrors | null {
    const query = control.value?.split('?')[1];
    const parsedQuery = queryString.parse(query);
    let errorMessage = $localize`:@@validators.incorrect_url:Incorrect URL`;

    const domain = parsedQuery.ld_id?.length > 0;
    delete parsedQuery.ld_id;
    const tpId = parsedQuery.tp_id?.length > 0;
    delete parsedQuery.tp_id;

    const queryValues = Object.values(parsedQuery);

    const offerId = queryValues?.find((value) => value === '{offer_id}');
    const affiliateId = queryValues?.find((value) => value === '{affiliate_id}');
    const advertiserId = queryValues?.find((value) => value === '{advertiser_id}');

    if (!domain) {
      errorMessage += $localize`:@@validators.ld_id_must_have_symbol:, domain (ld_id) must have at least 1 symbol`;
    }
    if (!tpId) {
      errorMessage += $localize`:@@validators.tp_id_must_have_symbol:, tp_id must have at least 1 symbol`;
    }
    if (!offerId) {
      errorMessage += $localize`:@@validators.offer_id_must_be:, offer ID must be {offer_id}`;
    }
    if (!affiliateId) {
      errorMessage += $localize`:@@validators.affiliate_id_must_be:, affiliate ID must be {affiliate_id}`;
    }
    if (!advertiserId) {
      errorMessage += $localize`:@@validators.advertiser_id_must_be:, advertiser ID must be {advertiser_id}`;
    }

    return domain && tpId && offerId && affiliateId && advertiserId ? null : { customValidate: errorMessage };
  }

  static url(control: AbstractControl): ValidationErrors | null {
    const errorMessage = $localize`:@@validators.incorrect_url_must_start_with:Incorrect URL, link must start with "http://" or "https://"`;
    const url = control.value;
    if (!url) return null;
    return url.startsWith('http://') || url.startsWith('https://') ? null : { customValidate: errorMessage };
  }

  static urlValidator(control: AbstractControl): ValidationErrors | null {
    const errorMessage = $localize`:@@validators.url_is_invalid:URL is invalid`;
    const url: string = control.value?.toString();
    if (!url) return null;
    const pattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
      'i'
    );
    return pattern.test(url) ? null : { customValidate: errorMessage };
  }

  static positiveAndIntegerNumber(control: AbstractControl): ValidationErrors | null {
    const value = ('' + control.value) as string;
    switch (true) {
      case value.includes('.') || value.includes(','):
        return { customValidate: `Number must be integer` };
      case +value < 0:
        return { customValidate: `Number must be positive` };
      default:
        return null;
    }
  }

  static number(control: AbstractControl): ValidationErrors | null {
    switch (control.value) {
      case null:
      case undefined:
      case '':
        return null;
    }
    const pattern = new RegExp('^([+]?)[0-9]+$');
    return pattern.test(control.value + '') ? null : { customValidate: 'Value should be a number' };
  }

  static passwordConfirm(formGroup: UntypedFormGroup): ValidationErrors | null {
    const newPassword = formGroup.controls.password;
    const newPasswordConfirmation = formGroup.controls.confirm_password;

    if (newPassword.value !== newPasswordConfirmation.value) {
      newPasswordConfirmation.setErrors({ customValidate: 'Passwords mismatch' });
      return { customValidate: 'Passwords mismatch' };
    }
    newPasswordConfirmation.setErrors(null);
    return null;
  }

  static passwordConfirmSetRequiredAsLeastOne(formGroup: UntypedFormGroup): ValidationErrors | null {
    switch (undefined) {
      case formGroup.controls.password:
      case formGroup.controls.confirm_password:
        return null;
    }
    const newPassword = formGroup.controls.password;
    const newPasswordConfirmation = formGroup.controls.confirm_password;
    const clearField = (field: AbstractControl) => {
      field.clearValidators();
      field.setErrors(null);
    };

    if (!newPassword.value && !newPasswordConfirmation.value) {
      clearField(newPasswordConfirmation);
      return null;
    }

    if (newPassword.value !== newPasswordConfirmation.value) {
      newPasswordConfirmation.setErrors({ customValidate: 'Passwords mismatch' });
      newPasswordConfirmation.setValidators(Validators.required);
      return { customValidate: 'Passwords mismatch' };
    }

    if (newPassword.value === newPasswordConfirmation.value) {
      clearField(newPasswordConfirmation);
      return null;
    }

    clearField(newPasswordConfirmation);
    return null;
  }

  static duplicateConditions(control: AbstractControl): ValidationErrors | null {
    const buffer = {};
    for (const { key } of control.value) {
      switch (true) {
        case buffer[key]:
          return { duplicateConditions: true };
        case !!key:
          buffer[key] = true;
          break;
      }
    }
    return null;
  }

  static uri(control: AbstractControl): ValidationErrors | null {
    const pattern = new RegExp(/(^\/)[\w./%=&#@*(){}+?!^$-\\]+$/i);
    const value = control.value;

    return pattern.test(value) ? null : { customValidate: 'Incorrect URI' };
  }

  static unsignedIntString(control: AbstractControl): ValidationErrors | null {
    return control.value?.filter((number) => number < 1 || number > 2147483647)?.length
      ? { customValidate: 'All entries should be in range from 1 to 2147483647' }
      : null;
  }

  static earningFormula(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const includesField = value?.includes('{payout_with_transactions_amount}');
    const includesSign = ['/', '*', '×', '÷'].some((sign) => value?.includes(sign));
    const includesNumbers = new RegExp(/\d/).test(value);
    const result = includesField && !includesSign && !includesNumbers;

    return result ? null : { customValidate: 'Incorrect formula' };
  }

  static ip(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const pattern = new RegExp(
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
    );
    return pattern.test(value) ? null : { customValidate: 'Incorrect IP' };
  }

  static dateRangeLessThanOneDay(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value || !value.from || !value.to) {
      return null;
    }

    const differenceInMilliseconds = value.to.diff(value.from);

    if (Math.abs(differenceInMilliseconds) > 86400000) {
      return { dateRangeExceedsOneDay: true };
    }

    return null;
  }
}
